import { useState, useEffect } from 'react';
import {
  makeStyles,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Collapse,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { parseDiff, Diff, Hunk, tokenize, markEdits } from 'react-diff-view';
import 'react-diff-view/style/index.css';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.background.default
      : theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    maxHeight: 500,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    // Override react-diff-view CSS variables for dark mode
    ...(theme.palette.type === 'dark' && {
      '--diff-background-color': theme.palette.background.paper,
      '--diff-text-color': theme.palette.text.primary,
      '--diff-code-insert-background-color': '#1a4d2e',
      '--diff-code-insert-edit-background-color': '#2d6a4f',
      '--diff-code-delete-background-color': '#4a1d1d',
      '--diff-code-delete-edit-background-color': '#6b2c2c',
      '--diff-gutter-insert-background-color': '#1e5631',
      '--diff-gutter-delete-background-color': '#5c2121',
    }),
    '& .diff': {
      fontSize: '12px',
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    },
    '& .diff-gutter': {
      minWidth: 40,
      padding: '0 8px',
    },
    '& .diff-gutter-col': {
      width: 40,
    },
    '& .diff-code': {
      padding: '0 8px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.background.default
      : theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  closeButton: {
    padding: theme.spacing(0.5),
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  error: {
    padding: theme.spacing(2),
    color: theme.palette.error.main,
  },
  fileHeader: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[200],
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    fontSize: '12px',
    fontWeight: 500,
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  },
  noChanges: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

interface DiffViewerProps {
  diffText: string | null;
  loading: boolean;
  error: string | null;
  open: boolean;
  onClose: () => void;
  filesChanged: number;
  additions: number;
  deletions: number;
}

export const DiffViewer = ({
  diffText,
  loading,
  error,
  open,
  onClose,
  filesChanged,
  additions,
  deletions,
}: DiffViewerProps) => {
  const classes = useStyles();
  const [files, setFiles] = useState<ReturnType<typeof parseDiff>>([]);

  useEffect(() => {
    if (diffText) {
      try {
        const parsed = parseDiff(diffText);
        setFiles(parsed);
      } catch (e) {
        console.error('Failed to parse diff:', e);
        setFiles([]);
      }
    } else {
      setFiles([]);
    }
  }, [diffText]);

  const renderFile = (file: ReturnType<typeof parseDiff>[0]) => {
    const { oldPath, newPath, hunks, type } = file;

    // Determine the file path to display
    let displayPath = newPath || oldPath || 'Unknown file';
    if (type === 'delete') {
      displayPath = `${oldPath} (deleted)`;
    } else if (type === 'add') {
      displayPath = `${newPath} (new file)`;
    } else if (oldPath !== newPath && oldPath && newPath) {
      displayPath = `${oldPath} → ${newPath}`;
    }

    // Tokenize hunks for better diff rendering
    let tokens;
    try {
      tokens = tokenize(hunks, {
        highlight: false,
        enhancers: [markEdits(hunks, { type: 'block' })],
      });
    } catch {
      tokens = undefined;
    }

    return (
      <Box key={file.newPath || file.oldPath}>
        <Box className={classes.fileHeader}>{displayPath}</Box>
        {hunks.length > 0 ? (
          <Diff viewType="unified" diffType={file.type} hunks={hunks} tokens={tokens}>
            {(hunks) => hunks.map(hunk => (
              <Hunk key={hunk.content} hunk={hunk} />
            ))}
          </Diff>
        ) : (
          <Box className={classes.noChanges}>No changes in this file</Box>
        )}
      </Box>
    );
  };

  return (
    <Collapse in={open}>
      <Paper className={classes.container} elevation={0}>
        <Box className={classes.header}>
          <Typography variant="subtitle2">
            {filesChanged} files changed (+{additions} -{deletions})
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            className={classes.closeButton}
            aria-label="Close diff"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className={classes.content}>
          {loading && (
            <Box className={classes.loading}>
              <CircularProgress size={24} />
              <Typography variant="body2" style={{ marginLeft: 8 }}>
                Loading diff...
              </Typography>
            </Box>
          )}

          {error && (
            <Box className={classes.error}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          {!loading && !error && files.length === 0 && diffText && (
            <Box className={classes.noChanges}>
              <Typography variant="body2">No changes to display</Typography>
            </Box>
          )}

          {!loading && !error && files.map(renderFile)}
        </Box>

        <Box className={classes.footer}>
          <IconButton
            size="small"
            onClick={onClose}
            className={classes.closeButton}
            aria-label="Close diff"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Collapse>
  );
};
